// components/VerifyThroughArgyle.tsx
//
// Parallels VerifyThroughPlaid. Renders a trigger button + a consent dialog +
// an ArgyleIncomeLink, gated on the per-scope Argyle feature flag. Visually
// matches the Plaid variant so ProviderToggle can swap between them without
// layout shifts.
import React, {CSSProperties, forwardRef, ReactNode, useEffect, useMemo, useState} from "react";
import {useCreate, useGetOne, useNotify} from "react-admin";
import {ARGYLE_BANK_PENDING_HINT_PREFIX, argyleCopy, argyleResources} from '../../../utils/argyleConfig';
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import InputMask from "react-input-mask";
import {scrollToAnchor} from "./scrollToAnchor";
import ArgyleIncomeLink from './ArgyleIncomeLink';
import StopSignIcon from "../components/StopSignIcon";

const MaskedInput = InputMask as React.ComponentType<any>;
const ARGYLE_ENABLEMENT_RESOURCES = {
  bank: 'argyle/argyleBankIncomeEnabled',
  payroll: 'argyle/argylePayrollIncomeEnabled',
} as const;

type Props = {
  trn_id?: string | null;
  triggerLabel?: string;
  includeRelinkWarning?: boolean;
  linkType?: "bank" | "payroll";
  onDone?: () => void;
  sourceId?: number;
  triggerContainerClassName?: string;
  triggerContainerStyle?: CSSProperties;
  triggerRowClassName?: string;
  triggerRowStyle?: CSSProperties;
  triggerButtonClassName?: string;
  triggerButtonStyle?: CSSProperties;
  triggerIcon?: ReactNode;
  disabled?: boolean;
  onFlowStarted?: () => void;
  onArgyleSuccess?: () => void;
  onFlowExited?: (completed: boolean) => void;
  onBankRemoved?: () => void;
};

function formatDobForDateInput(raw: any): string {
  if (!raw) return '';
  const rawText = String(raw).trim();
  if (rawText.startsWith('1900-01-01')) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  if (y <= 1900) return '';
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${m}/${day}/${y}`;
}

function normalizeStateCode(raw: any): string {
  return String(raw || '').trim().toUpperCase();
}

function resolveStateCode(raw: any, stateOptions: Array<{ code: string; name: string }>): string {
  const normalized = normalizeStateCode(raw);
  if (!normalized) return '';
  if (/^[A-Z]{2}$/.test(normalized)) return normalized;
  const matched = stateOptions.find((item) => item.name === normalized || item.code === normalized);
  return matched?.code || '';
}

function parseMMDDYYYY(raw: string): Date | null {
  const trimmed = String(raw || '').trim();
  if (!trimmed || trimmed.includes('_')) return null;
  const parts = trimmed.split('/');
  if (parts.length !== 3) return null;
  const [month, day, year] = parts.map(Number);
  if (!month || !day || !year || year < 1901) return null;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime()) || date.getMonth() !== month - 1) return null;
  return date;
}

function isDobValueValid(raw: string): boolean {
  const date = parseMMDDYYYY(raw);
  if (!date) return false;
  if (date.getFullYear() <= 1900) return false;
  const today = new Date();
  const maxYear = today.getFullYear() - 18;
  const max = new Date(maxYear, today.getMonth(), today.getDate());
  return date <= max;
}


function getBorrowerPrefill(proofOfIdsData: any, stateOptions: Array<{ code: string; name: string }>) {
  const idData = proofOfIdsData?.identification_data;
  return {
    dob: formatDobForDateInput(idData?.DOB),
    state: resolveStateCode(idData?.state, stateOptions),
  };
}

export const VerifyThroughArgyle = forwardRef<HTMLDivElement, Props>(
  ({
    trn_id,
    triggerLabel,
    includeRelinkWarning = false,
    linkType = "bank",
    onDone,
    sourceId,
    triggerContainerClassName,
    triggerContainerStyle,
    triggerRowClassName,
    triggerRowStyle,
    triggerButtonClassName,
    triggerButtonStyle,
    triggerIcon,
    disabled = false,
    onFlowStarted,
    onArgyleSuccess,
    onFlowExited,
    onBankRemoved,
  }, ref) => {
    const enablementResource = ARGYLE_ENABLEMENT_RESOURCES[linkType] ?? ARGYLE_ENABLEMENT_RESOURCES.bank;
    const { data: argyleIncomeEnabled, isLoading } = useGetOne(
      enablementResource,
      { id: trn_id },
      { enabled: trn_id != null, refetchOnWindowFocus: true }
    );

    const { data: proofOfIdsData } = useGetOne(
      "proofOfIdsData",
      { id: trn_id },
      { enabled: trn_id != null }
    );

    const [create, { isLoading: isSaving }] = useCreate();
    const notify = useNotify();

    const [altOpen, setAltOpen] = useState(false);
    const [borrowerDialogOpen, setBorrowerDialogOpen] = useState(false);
    const [dobValue, setDobValue] = useState('');
    const [stateValue, setStateValue] = useState('');
    const [lastSavedBorrowerFields, setLastSavedBorrowerFields] = useState<{ dob: string; state: string } | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    const stateOptions = useMemo(
      () =>
        (proofOfIdsData?.states || [])
          .map((item: any) => ({
            code: normalizeStateCode(item?.StateShortCode),
            name: normalizeStateCode(item?.StateName),
            label: `${item?.StateShortCode || ''} - ${item?.StateName || ''}`.trim(),
          }))
          .filter((item: any) => item.code && item.name && item.label),
      [proofOfIdsData?.states]
    );
    const borrowerPrefill = useMemo(
      () => getBorrowerPrefill(proofOfIdsData, stateOptions),
      [proofOfIdsData?.identification_data?.DOB, proofOfIdsData?.identification_data?.state, stateOptions]
    );

    const getEffectiveBorrowerValues = () => {
      const fallback = lastSavedBorrowerFields || { dob: '', state: '' };
      return {
        dob: borrowerPrefill.dob || fallback.dob,
        state: borrowerPrefill.state || fallback.state,
      };
    };

    const openBorrowerDialog = () => {
      const effective = getEffectiveBorrowerValues();
      setDobValue(effective.dob);
      setStateValue(effective.state);
      setSaveError(null);
      setBorrowerDialogOpen(true);
    };

    const handleOpenAlt = () => {
      if (linkType === 'bank') {
        openBorrowerDialog();
        return;
      }
      setAltOpen(true);
    };

    const handleCloseAlt = () => {
      setAltOpen(false);
    };

    const handleMissingBorrowerFields = (fields: string[]) => {
      const normalized = (fields || [])
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      const canRecoverViaBorrowerDialog =
        normalized.length > 0 &&
        normalized.every((field) => field === 'birth_date' || field === 'state' || field === 'address');

      setAltOpen(false);
      if (canRecoverViaBorrowerDialog) {
        openBorrowerDialog();
        return;
      }

      notify(argyleResources[linkType].notifyError, { type: 'error' });
    };

    const handleBorrowerFieldsSaved = () => {
      setLastSavedBorrowerFields({
        dob: dobValue,
        state: normalizeStateCode(stateValue),
      });
      setBorrowerDialogOpen(false);
      setAltOpen(true);
    };

    const handleBorrowerSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      setSaveError(null);
      if (!isDobValueValid(dobValue)) {
        setSaveError('Please enter a valid date of birth. Applicant must be at least 18 years old.');
        return;
      }
      const payload: Record<string, any> = {
        trn_id,
        dob: dobValue,
        state: normalizeStateCode(stateValue),
      };
      create(
        'argyleBorrowerFields',
        { data: payload },
        {
          onSuccess: handleBorrowerFieldsSaved,
          onError: () => setSaveError('Failed to save. Please try again.'),
        }
      );
    };

    const handleGoToEmploymentIncome = (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setAltOpen(false);
      scrollToAnchor(`employment-income-anchor-${trn_id}`, 3000);
    };

    const handleLinkClicked = () => {
      if (linkType === "bank" && trn_id && typeof window !== "undefined") {
        sessionStorage.setItem(
          `${ARGYLE_BANK_PENDING_HINT_PREFIX}${trn_id}`,
          String(Date.now())
        );
      }
      if (linkType === "payroll") {
        handleCloseAlt();
      }
      onFlowStarted?.();
    };

    const handleFlowExited = (completed: boolean) => {
      setAltOpen(false);
      onFlowExited?.(completed);
    };

    const handleArgyleDone = () => {
      if (linkType === "payroll") {
        onDone?.();
        return;
      }
      setAltOpen(false);
      scrollToAnchor("bank-income-anchor", 3000);
      onDone?.();
    };

    useEffect(() => {
      if (!borrowerDialogOpen) return;
      if (!dobValue && borrowerPrefill.dob) {
        setDobValue(borrowerPrefill.dob);
      }
      if (!stateValue && borrowerPrefill.state) {
        setStateValue(borrowerPrefill.state);
      }
    }, [borrowerDialogOpen, borrowerPrefill.dob, borrowerPrefill.state, dobValue, stateValue]);

    const isExplicitlyDisabled = argyleIncomeEnabled?.enabled === false;
    if (!trn_id || isLoading || isExplicitlyDisabled) return null;

    const triggerText = triggerLabel ?? 'Provide proof another way';

    const showBankWarning = linkType === "bank";
    const copyVariant = argyleCopy[linkType];
    const dialogBody = (
      <div style={{ lineHeight: 1.5 }}>
        <p>{copyVariant.actionDescription}</p>
        <br />
        <p>{copyVariant.actionWarning}</p>
        {includeRelinkWarning && (
          <>
            <br />
            <p>{copyVariant.actionRelinkWarning}</p>
          </>
        )}
      </div>
    );

    const containerClassName =
      triggerContainerClassName || "d-flex flex-column align-items-end justify-content-begin";
    const containerStyle = triggerContainerStyle || { width: "100%" };
    const rowClassName = triggerRowClassName || "d-flex justify-content-begin";
    const rowStyle = triggerRowStyle || { width: "100%", marginTop: "8px" };
    const buttonClassName =
      triggerButtonClassName || "btn btn-link p-0 text-decoration-none";
    const buttonStyle = triggerButtonStyle || { fontSize: "15px" };

    return (
      <div ref={ref} className={containerClassName} style={containerStyle}>
        <div className={rowClassName} style={rowStyle}>
          <button
            type="button"
            className={buttonClassName}
            style={buttonStyle}
            onClick={handleOpenAlt}
            disabled={disabled}
            aria-label={triggerText}
          >
            {triggerIcon ? (
              <>
                {triggerIcon}
                <span style={{ fontFamily: "inherit", fontWeight: "inherit", fontSize: "inherit" }}>
                  {triggerText}
                </span>
              </>
            ) : (
              triggerText
            )}
          </button>
        </div>

        {/* Borrower fields dialog */}
        <Dialog
          open={borrowerDialogOpen}
          onClose={() => !isSaving && setBorrowerDialogOpen(false)}
          aria-labelledby="borrower-fields-title"
          disableScrollLock
          PaperProps={{ style: { maxWidth: 320, width: '100%' } }}
        >
          <form onSubmit={handleBorrowerSubmit}>
            <DialogContent>
              <h2
                id="borrower-fields-title"
                style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 }}
              >
                A few details are needed before linking your bank
              </h2>

              <div className="mb-3">
                <label className="form-label" htmlFor="borrower-dob">
                  Date of Birth <span className="text-danger">*</span>
                </label>
                <MaskedInput
                  mask="99/99/9999"
                  id="borrower-dob"
                  className="form-control cus-input-control"
                  value={dobValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDobValue(e.target.value)}
                  placeholder="MM/DD/YYYY"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="borrower-state">
                  State <span className="text-danger">*</span>
                </label>
                <select
                  id="borrower-state"
                  className="form-control cus-input-control"
                  value={stateValue}
                  onChange={(e) => setStateValue(normalizeStateCode(e.target.value))}
                  required
                >
                  <option value="">Select state</option>
                  {stateOptions.map((item: any) => (
                    <option key={item.code} value={item.code}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              {saveError && (
                <p style={{ color: '#b42318', marginTop: 4, marginBottom: 0, fontSize: '0.8rem' }}>{saveError}</p>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => setBorrowerDialogOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Save & Continue
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Consent dialog */}
        <Dialog
          open={altOpen}
          onClose={handleCloseAlt}
          aria-labelledby="verify-argyle-title"
          aria-describedby="verify-argyle-desc"
          disableScrollLock
          PaperProps={{ style: { maxWidth: 420 } }}
        >
          <DialogContent id="verify-argyle-desc">
            {showBankWarning && (
              <div
                className="d-flex align-items-start"
                style={{
                  gap: "10px",
                  marginBottom: "0.75rem",
                  padding: "10px 12px",
                  borderRadius: 8,
                  backgroundColor: "#fff3cd",
                  color: "#664d03",
                  lineHeight: 1.5,
                }}
              >
                <StopSignIcon size={24} />
                <div>
                  Do not proceed if you have already uploaded original pdf paystubs.
                  If you have them available to upload, you can do so{" "}
                  <a href={`#employment-income-anchor-${trn_id}`} onClick={handleGoToEmploymentIncome}>
                    here
                  </a>
                  .
                </div>
              </div>
            )}
            <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.5rem" }}>
              <h2
                id="verify-argyle-title"
                style={{ margin: 0, fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 }}
              >
                Verify your income
              </h2>
            </div>
            {dialogBody}
          </DialogContent>

          <DialogActions className="argyle-dialog-actions">
            <div className="argyle-dialog-actions__item">
              <button
                type="button"
                onClick={handleCloseAlt}
                className="close-btn"
              >
                Close
              </button>
            </div>

            <div className="argyle-dialog-actions__item">
              <ArgyleIncomeLink
                type={linkType}
                trn_id={trn_id}
                linkTokenData={sourceId ? { source_id: sourceId } : undefined}
                onLinkClicked={handleLinkClicked}
                onArgyleSuccess={onArgyleSuccess}
                onArgyleExit={handleFlowExited}
                onDone={handleArgyleDone}
                onMissingBorrowerFields={linkType === 'bank' ? handleMissingBorrowerFields : undefined}
                onBankRemoved={linkType === 'bank' ? onBankRemoved : undefined}
                subtitle=""
                label="Continue"
                asButton
                disabled={disabled}
                className="argyle-btn"
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  minWidth: 0,
                }}
              />
            </div>
          </DialogActions>

          <style>{`
            .argyle-dialog-actions {
              display: flex;
              gap: 4px;
              width: 100%;
              align-items: stretch;
              justify-content: space-between;
            }
            .argyle-dialog-actions__item {
              flex: 1 1 0;
              display: flex;
              min-width: 0;
            }
            .argyle-dialog-actions__item > * {
              flex: 1;
              min-width: 0;
            }
            .argyle-btn {
              padding: 10px 32px;
              border: 1px solid #0a7a4d;
              border-radius: 8px;
              background: #0a7a4d;
              color: #fff;
              font-weight: 600;
              font-size: 0.95rem;
              text-decoration: none;
              cursor: pointer;
              box-shadow: 0 1px 2px rgba(0,0,0,0.08);
              transition: background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .05s ease;
              white-space: nowrap;
              flex: 1;
              min-width: 0;
              display: inline-flex;
              align-items: center;
              justify-content: center;
            }
            .argyle-btn:hover {
              background: #086640;
              border-color: #086640;
              box-shadow: 0 2px 6px rgba(0,0,0,0.18);
              color: #fff !important;
              text-decoration: none !important;
            }
            .argyle-btn:active {
              background: #05502f;
              border-color: #05502f;
              transform: translateY(1px);
              box-shadow: 0 1px 2px rgba(0,0,0,0.12);
              color: #fff !important;
              text-decoration: none !important;
            }
            .close-btn {
              padding: 10px 16px;
              border: 1px solid #cbd5e1;
              border-radius: 8px;
              background: #fff;
              color: #334155;
              font-weight: 600;
              font-size: 0.95rem;
              text-decoration: none;
              cursor: pointer;
              box-shadow: 0 1px 2px rgba(0,0,0,0.06);
              transition: background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .05s ease;
              width: 100%;
              display: inline-flex;
              flex: 1;
              min-width: 0;
              align-items: center;
              justify-content: center;
            }
            .close-btn:hover {
              background: #f8fafc;
              border-color: #94a3b8;
              box-shadow: 0 2px 6px rgba(0,0,0,0.12);
            }
            .close-btn:active {
              background: #f1f5f9;
              border-color: #94a3b8;
              transform: translateY(1px);
              box-shadow: 0 1px 2px rgba(0,0,0,0.10);
            }
            #argyle-borrower-dob-portal {
              position: relative;
              z-index: 1600 !important;
            }
            .argyle-borrower-dob-popper,
            .react-datepicker-popper {
              z-index: 1600 !important;
            }
          `}</style>
        </Dialog>
      </div>
    );
  }
);

VerifyThroughArgyle.displayName = 'VerifyThroughArgyle';

export default VerifyThroughArgyle;
